import { useEffect, useRef } from "react"

import ChatBubble from "./ChatBubble"
import { cn } from "@/lib/utils"

// A centered day divider ("Today", "Yesterday", "Apr 2").
function DayDivider({ label }) {
  return (
    <div className="flex items-center gap-3 text-[11px] font-bold text-ink-3 before:h-px before:flex-1 before:bg-border after:h-px after:flex-1 after:bg-border">
      {label}
    </div>
  )
}

// A system / status line inside the thread (e.g. "Project on hold — resume any
// time"). Pill-shaped, centered.
function SystemLine({ text }) {
  return (
    <div className="self-center rounded-full border border-border bg-secondary px-3.5 py-[5px] text-center text-[11.5px] text-ink-3">
      {text}
    </div>
  )
}

// How close to the bottom (px) still counts as reading the newest message.
const NEAR_BOTTOM = 80

// The scrollable message list shared by the order Chat tab and the Messages
// inbox. `items` is a normalized stream: each entry is a day divider, a system
// line, or a message bubble. Jumps to the newest message when a thread opens,
// when the customer sends one, and when one arrives while they are already at
// the bottom. Callers rebuild `items` on every render and refresh, so the jump
// keys on the first and last message rather than the array: a customer
// scrolled up to read earlier messages is left where they are.
//
//   { kind: "day", label }
//   { kind: "system", text }
//   { kind: "message", id, mine, who, role, at, body, files?, images? }
export default function MessageThread({ items = [], empty, className, onRetry, onEdit, onDismiss }) {
  const ref = useRef(null)
  const atBottom = useRef(true)
  const shownFirst = useRef(undefined)
  const shownCount = useRef(0)

  const messages = items.filter((item) => item.kind === "message")
  const hasMessages = messages.length > 0
  const first = messages[0]
  const last = messages[messages.length - 1]
  const firstKey = first?.id ?? null
  const lastKey = last ? `${last.id}:${messages.length}` : null

  useEffect(() => {
    const node = ref.current
    if (!node) return

    // A different first message means a different thread (the inbox reuses
    // this list when the customer switches conversations).
    const newThread = shownFirst.current !== firstKey
    // Only a message added to the end is the customer's fresh send: a pending
    // bubble swapped for its saved copy, or a failed one removed, is not.
    const sentNow = messages.length > shownCount.current && last?.mine
    shownFirst.current = firstKey
    shownCount.current = messages.length

    if (newThread || atBottom.current || sentNow) {
      node.scrollTop = node.scrollHeight
      atBottom.current = true
    }
  }, [firstKey, lastKey]) // eslint-disable-line react-hooks/exhaustive-deps

  const onScroll = () => {
    const node = ref.current
    if (node) atBottom.current = node.scrollHeight - node.scrollTop - node.clientHeight <= NEAR_BOTTOM
  }

  return (
    <div
      ref={ref}
      onScroll={onScroll}
      className={cn("flex flex-1 flex-col gap-4 overflow-y-auto bg-surface3 p-[22px]", className)}
    >
      {hasMessages
        ? items.map((item, i) => {
            if (item.kind === "day") return <DayDivider key={`d-${i}`} label={item.label} />
            if (item.kind === "system") return <SystemLine key={`s-${i}`} text={item.text} />
            return (
              <ChatBubble
                key={item.id ?? `m-${i}`}
                mine={item.mine}
                who={item.who}
                role={item.role}
                at={item.at}
                body={item.body}
                files={item.files}
                images={item.images}
                pending={item.pending}
                error={item.error}
                onRetry={() => onRetry?.(item.id)}
                onEdit={() => onEdit?.(item.id)}
                onDismiss={() => onDismiss?.(item.id)}
              />
            )
          })
        : empty}
    </div>
  )
}
